import React from "react"
import { Link } from "react-router-dom"
import { formatDate, formatScore } from "../../lib/format"
import type { ProvaListItem } from "../../types"

/** Card-herói de uma prova. O card inteiro é um link pra /prova/:id (onde ficam
 *  o desempenho e o botão de iniciar). Afordância de clique: hover + seta. */
export function ProvaCard({ prova }: { prova: ProvaListItem }) {
  const done = prova.stats.total
  const days = prova.days_until_exam
  const hasActive = prova.active_attempt_id !== null

  return (
    <Link
      to={`/prova/${prova.id}`}
      className="group relative block overflow-hidden rounded-2xl bg-navy-900 p-6 shadow-lg shadow-navy-900/20 transition duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-navy-900/30 hover:ring-1 hover:ring-gold-400/40"
    >
      {/* divisas da marca, decorativas */}
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute -right-8 -top-8 h-44 w-44 text-white/[0.04]"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="square"
        aria-hidden="true"
      >
        <path d="M4 8.5 12 3.5 20 8.5" />
        <path d="M4 14 12 9 20 14" />
        <path d="M4 19.5 12 14.5 20 19.5" />
      </svg>

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <span className="inline-block rounded-md bg-gold-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold-400">
            {prova.school}
          </span>
          <h3 className="mt-2 text-xl font-bold text-white transition-colors group-hover:text-gold-300">{prova.name}</h3>
          <p className="mt-1.5 text-xs text-navy-200">
            {done > 0 ? (
              <>
                {done} {done === 1 ? "simulado feito" : "simulados feitos"}
                {prova.stats.best_score != null && (
                  <>
                    {" · melhor nota "}
                    <span className="font-bold text-white">{formatScore(prova.stats.best_score)}</span>
                  </>
                )}
              </>
            ) : (
              "Você ainda não treinou esta prova"
            )}
          </p>
        </div>

        {days != null && (
          <div className="shrink-0 rounded-xl border border-white/10 bg-white/5 px-5 py-3 text-center sm:min-w-[132px]">
            <p className="text-4xl font-extrabold leading-none text-gold-400 tabular-nums">{days}</p>
            <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-gold-500/90">dias pra prova</p>
            {prova.exam_date && <p className="mt-0.5 text-[11px] text-navy-300">{formatDate(prova.exam_date)}</p>}
          </div>
        )}
      </div>

      <div className="relative mt-5 flex items-center gap-1.5 border-t border-white/10 pt-4 text-sm font-semibold text-gold-400 transition-colors group-hover:text-gold-300">
        {hasActive ? "Continuar simulado" : prova.stats.total > 0 ? "Ver desempenho e treinar" : "Fazer meu diagnóstico"}
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.21 4.23a.75.75 0 011.06.02l5 5.25a.75.75 0 010 1.04l-5 5.25a.75.75 0 11-1.08-1.04L11.69 10 7.23 5.29a.75.75 0 01-.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </Link>
  )
}
